import {
  AsyncPipe,
  NgClass,
  NgTemplateOutlet,
  registerLocaleData,
} from '@angular/common';
import {
  Component,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import { ButtonComponent, SkeletonComponent } from '@kinomap-dev/ng-ui-lib';
import {
  NgbDropdown,
  NgbDropdownItem,
  NgbDropdownMenu,
  NgbDropdownToggle,
} from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { User } from 'app/models';
import {
  selectProfileData,
  selectProfileLoading,
} from 'app/store/me/me.selectors';
import { BehaviorSubject, filter, map, Observable, Subscription } from 'rxjs';
import { animations } from 'src/app/animations/menu-deploy';
import { menu } from 'src/app/configs/menu';
import {
  LanguageService,
  SwarmLoginService,
  UserService,
} from 'src/app/services';
import { ScrollingService } from 'src/app/services/scrolling.service';
import {
  ADMIN_SITE,
  ADMINV2_SITE,
  OLD_SITE,
} from 'src/environments/environment.local';

@Component({
  animations: [animations.bgOpacity, animations.menuSlide],
  encapsulation: ViewEncapsulation.None,
  imports: [
    NgClass,
    NgTemplateOutlet,
    NgbDropdown,
    NgbDropdownToggle,
    NgbDropdownMenu,
    NgbDropdownItem,
    RouterLink,
    AsyncPipe,
    TranslateModule,
    SkeletonComponent,
    ButtonComponent,
    RouterLinkActive,
  ],
  selector: 'app-navbar',
  standalone: true,
  styleUrls: ['./navbar.component.scss'],
  templateUrl: './navbar.component.html',
})
export class NavbarComponent implements OnInit, OnDestroy {
  private languageService: LanguageService = inject(LanguageService);
  private translate: TranslateService = inject(TranslateService);
  private router: Router = inject(Router);
  private userService: UserService = inject(UserService);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private scrollingService: ScrollingService = inject(ScrollingService);
  private readonly store: Store = inject(Store);
  public loginService: SwarmLoginService = inject(SwarmLoginService);

  private isAutoScroll: boolean = false;
  private scrollTargetId: string = '';
  private scrollTimeout: NodeJS.Timeout;
  private scrollToTargetIfTopPosition: any =
    this.scrollTopEventHandler.bind(this);
  // subscriptions
  private subscriptions: Subscription[] = [];
  // navigation data
  activeRoute$: BehaviorSubject<string>;
  // behavior on scroll & mouse mouve
  actualScrollTop: number = 0;
  adminSiteLink: string = ADMIN_SITE;
  adminv2SiteLink: string = ADMINV2_SITE;
  allAvailableLanguages: string[];
  // languages
  currentLanguage: string;
  // connexion
  user$: Observable<User>;
  loading$: Observable<boolean>;
  user: User;
  hasTransparentMode: boolean = true;
  hide: boolean = false;
  // responsive properties
  windowSize$: BehaviorSubject<number> = new BehaviorSubject(window.innerWidth);
  isBigScreen$: Observable<boolean> = this.windowSize$.pipe(
    map((size: number) => {
      return size > 1400;
    })
  );
  isMobile$: Observable<boolean> = this.windowSize$.pipe(
    map((size: number) => {
      return size <= 950;
    })
  );
  // menu
  menu: any = menu;
  mobileMenuOpen: boolean = false;
  oldSiteLink: string = OLD_SITE;
  // links
  searchContentLink: string = 'browse';
  transparent: boolean = this.hasTransparentMode;
  userMenu: any = [];

  @HostListener('window:mousemove', ['$event'])
  onMouseMove({ clientY }: MouseEvent): void {
    if (clientY <= 65) {
      this.hide = false;
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize({ target }: { target: Window }): void {
    this.windowSize$.next(target.innerWidth);
    this.mobileMenuOpen = false;
  }

  @HostListener('window:scroll', ['$event'])
  onScroll({ target }: { target: Document }): void {
    const scrollTop: number = target.scrollingElement.scrollTop;
    const route: ActivatedRoute = this.getChild(this.route);
    this.hide =
      scrollTop > this.actualScrollTop
        ? !route.snapshot?.data?.permanentBanner
        : false;
    this.actualScrollTop = scrollTop;
    this.transparent = scrollTop < 200 ? this.hasTransparentMode : false;

    if (!this.isAutoScroll && this.activeRoute$.getValue() === 'pricing') {
      this.activeRoute$.next('home');
    }

    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      this.isAutoScroll = false;
    }, 100);
  }

  isActiveRoute(route: string, item: any): boolean {
    const cleanUrl = (str: string): string =>
      str?.replace(/^\/+|\/+$/g, '') || 'home';
    const cleanRoute: string = cleanUrl(route);
    const cleanLink: string = cleanUrl(item.link);

    return item.scrollTo
      ? cleanRoute.includes(item.scrollTo)
      : cleanRoute === cleanLink;
  }

  private displayMenu(): void {
    const route: ActivatedRoute = this.route.root;
    let contextualMenu: any = route.snapshot?.data?.menu;
    let children: any;
    let currentRoute: ActivatedRoute = route;
    // Get last contextual menu
    do {
      children = currentRoute.children;
      for (const child of children) {
        const routeMenu: any = child.snapshot.data.menu;
        if (routeMenu) {
          contextualMenu = routeMenu;
        }
      }
      if (children.length) {
        currentRoute = children[0];
      }
    } while (children?.length);

    if (contextualMenu) {
      return (this.menu = contextualMenu);
    }
    this.menu = menu;
  }

  private getChild(activatedRoute: ActivatedRoute): ActivatedRoute {
    if (activatedRoute.firstChild) {
      return this.getChild(activatedRoute.firstChild);
    }
    return activatedRoute;
  }

  private getRouterSubscriptions(): Subscription[] {
    return [
      this.router.events
        .pipe(filter((event: NavigationEnd) => event instanceof NavigationEnd))
        .subscribe((nav: NavigationEnd) => {
          this.activeRoute$.next(nav.url);
          this.setTransparentMode();
          this.displayMenu();
        }),
    ];
  }

  private setMenu(): void {
    this.userMenu = [
      {
        name: 'menu.dashboard',
        route: '/dashboard',
        type: 'internal-link', // 'internal-link' stands for route exposed by angular internal router
      },
      {
        link: null,
        name: 'hr',
      },
      {
        name: 'menu.profil.profil',
        route: '/dashboard/my-profile',
        type: 'internal-link',
      },
      {
        name: 'menu.profil.settings',
        route: '/dashboard/setting',
        type: 'internal-link',
      },
      {
        name: 'menu.profil.mySubscriptions',
        route: '/dashboard/subscriptions',
        type: 'internal-link',
      },
      {
        name: 'menu.profil.payementMethods',
        route: '/dashboard/setting/payment-methods',
        type: 'internal-link',
      },
      { link: null, name: 'hr' },
      {
        link: '/dashboard/my-audience',
        name: 'menu.profil.myAudience',
        type: 'external-link',
      },
      {
        name: 'menu.profil.trainingStats',
        route: '/dashboard/mytraining',
        type: 'internal-link',
      },
    ];
  }

  private scrollTopEventHandler(): void {
    if (window.scrollY === 0) {
      this.scrollingService.detectChangesAndScrollSmoothlyTo(
        this.scrollTargetId
      );

      window.removeEventListener('scroll', this.scrollToTargetIfTopPosition);
    }
  }

  private setTransparentMode(): void {
    const route: ActivatedRoute = this.getChild(this.route);
    this.hasTransparentMode = !!route.snapshot?.data?.banner;
    this.transparent = window.scrollY < 200 ? this.hasTransparentMode : false;
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  disconnect(): void {
    this.userService.disconnect();
    this.router.navigateByUrl('/home');
  }

  getLanguage(lang: string): string {
    return lang.split('-')[1];
  }

  navigateTo(link: string, internal = true, scroll?: string): void {
    this.mobileMenuOpen = false;
    if (scroll) {
      this.scrollTargetId = scroll;
      this.isAutoScroll = true;
      this.activeRoute$.next(scroll);

      if (this.router.url !== `/${link}`) {
        this.router.navigateByUrl('home');
        setTimeout((): void => {
          if (window.scrollY === 0) {
            this.scrollingService.detectChangesAndScrollSmoothlyTo(scroll);
            return;
          }
          window.addEventListener('scroll', this.scrollToTargetIfTopPosition);
        }, 100);
        return;
      }

      const target: HTMLElement = document.getElementById(scroll);
      target.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    if (this.router.url === `/${link}`) {
      window.scroll({ behavior: 'smooth', top: 0 });
      return;
    }

    if (internal) {
      this.router.navigateByUrl(link);
      return;
    }

    window.location.href = link;
  }

  switchLang(lang: string): void {
    this.translate.use(lang);
    registerLocaleData(lang);
    this.currentLanguage = lang;
    this.languageService.set(lang);

    // Update URL with language parameter for SEO
    const url: URL = new URL(window.location.href);
    url.searchParams.set('lang', lang);
    window.history.replaceState({}, '', url.toString());
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }

  ngOnInit(): void {
    this.allAvailableLanguages = this.translate.getLangs();
    this.currentLanguage = this.languageService.get()
      ? this.languageService.get()
      : this.translate.defaultLang;

    this.user$ = this.store.select(selectProfileData);
    this.loading$ = this.store.select(selectProfileLoading);

    // Check for language parameter in URL
    const urlParams: URLSearchParams = new URLSearchParams(
      window.location.search
    );
    const langFromUrl: string = urlParams.get('lang');
    if (langFromUrl && this.allAvailableLanguages.includes(langFromUrl)) {
      this.switchLang(langFromUrl);
    }

    this.subscriptions.push(
      this.user$.subscribe((user: User) => {
        this.user = user;

        if (!user) {
          this.userMenu = [];
          return;
        }

        this.setMenu();
      })
    );
    this.activeRoute$ = new BehaviorSubject(this.router.url);
    this.setTransparentMode();
    this.displayMenu();
    this.subscriptions.push(...this.getRouterSubscriptions());
  }
}
